<template>
  <Suspense>
    <!-- component with nested async dependencies -->
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <!-- loading state via #fallback slot -->
    <template #fallback> Loading... </template>
  </Suspense>
</template>

<script setup>
import "@/assets/primeVueTheme/themes/mytheme/theme.scss"

import { useRoute } from "vue-router";

import * as Sentry from "@sentry/vue";
import * as platform from 'platform';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

import { useBrandingStore } from "~/stores/branding";
import { useCartStore } from "~/stores/cart";
import { useSessionStore } from "~/stores/session";
import Network from "~/utils/network";
import { useBnplApplicationStore } from "~/stores/bnplapplication";
import { useUserStore } from "~/stores/user";
import { useSelfcareStore } from "~/stores/selfcare";
import { loadAllMessages, loadPublicMessages, setI18nLanguage, setFormats } from "~/utils/i18n";
import pixels from "@/utils/pixels";
import { i18nInstance } from "~/plugins/i18n";
import { usePrescoreStore } from "./stores/prescore";
import { useCountryStore } from "./stores/country";
import { useFingerprintStore } from "./stores/fingerprint";
import { usePixelStore } from "./stores/pixel";

const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const brandingStore = useBrandingStore();
const cartStore = useCartStore();
const userStore = useUserStore();
const sessionStore = useSessionStore();
const bnplApplicationStore = useBnplApplicationStore();
const selfcareStore = useSelfcareStore();
const prescoreStore = usePrescoreStore();
const countryStore = useCountryStore();
const fingerprintStore = useFingerprintStore();
const pixelStore = usePixelStore();

onMounted(async () => {
  await pixelStore.fetchFacebookPixelId(route.params.financer);
  useHead({
    script: [{ innerHTML: pixels.facebook.script(pixelStore.pixelId) }],
    noscript: [{ innerHTML: pixels.facebook.noscript(pixelStore.pixelId) }],
  })
})

watch(
  () => sessionStore.userId,
  (value) => {
    if (process.client) {
      Sentry.setUser({ id: value })
    }
  },
  { immediate: true, }
)

// Saving tech data for every new publicId
watch(
  [() => bnplApplicationStore.publicId, () => sessionStore.isLoggedIn, () => bnplApplicationStore.bnplApplication.currentFlowIndex],
  async (value) => {
    if (process.client) {
      const savedTechData = JSON.parse(localStorage.getItem("saved-tech-data")) ?? {};
      if (value[1] && value[2] && value[0] && !savedTechData[value[0]]) {
        await fingerprintStore.load();
        bnplApplicationStore.saveDynamicData({key: "techDataFingerprintId", value: fingerprintStore.visitorId});
        bnplApplicationStore.saveDynamicData({key: "techDataUserAgent", value: platform.description});
        try {
          const ipAndGeo = await $fetch(runtimeConfig.public.instacashHelperBaseUrl + "/get-ip");
          await bnplApplicationStore.saveDynamicData({key: "techDataIpAndGeo", value: JSON.parse(ipAndGeo)});
          savedTechData[value[0]] = true;
          localStorage.setItem("saved-tech-data", JSON.stringify(savedTechData));
        } catch {
          console.error("Couldn't get IP");
        }
      }
    }
  }
)

/* promise.all */
onServerPrefetch(async () => {
  const brandingUrl = process?.env?.BRANDING_URL || "http://localhost:3000/api/administration/branding/external/";
  const cartUrl = process?.env?.CART_URL || "http://localhost:3000/api/bnpl/external/cart/";
  const userUrl = process?.env?.USER_URL || "http://localhost:3000/api/bnpl/external/user/";
  const countryUrl = process?.env?.COUNTRY_URL || "http://localhost:3000/api/administration/external/country/financier/";

  if (route.params.financer) {
    const country = await $fetch(countryUrl + route.params.financer);
    countryStore.load(country);
  } else {
    console.log("No financier");
  }

  if (
    route.name === "financer-publicId" ||
    route.name === "financer" ||
    route.name === "financer-prescore" ||
    route.name === "financer-prescore-prescoreId" ||
    route.name === "financer-publicId-review" ||
    route.name === "financer-test" ||
    /^financer-\w*/g.test(route.name)
  ) {
    /* Get branding */
    try {
      console.log("Fetching branding at " + brandingUrl + route.params.financer);
      const colors = await $fetch(brandingUrl + route.params.financer);
      await brandingStore.load(colors);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "Branding could not be loaded - " + e.statusMessage,
      });
    }
  }

  if (route.name === "financer-publicId") {
    /* Get cart */
    try {
      console.log("Fetching cart at " + cartUrl + route.params.publicId);
      const cart = await $fetch(cartUrl + route.params.publicId);
      await cartStore.load(cart);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "Cart could not be loaded - " + e.statusMessage,
      });
    }

    /* Get user info */
    try {
      console.log("Fetching userInfo at " + userUrl + route.params.publicId);
      const userInfo = await $fetch(userUrl + route.params.publicId);
      await userStore.load(userInfo);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "User info could not be loaded - " + e.statusMessage,
      });
    }
  }

  if (route.name === "financer-publicId-review") {
    /* Get user info */
    try {
      console.log("Fetching userInfo at " + userUrl + route.params.publicId);
      const userInfo = await $fetch(userUrl + route.params.publicId);
      await userStore.load(userInfo);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "User info could not be loaded - " + e.statusMessage,
      });
    }
  }
});
onBeforeMount(() => {
  if (route.name === "financer-publicId") {
    bnplApplicationStore.publicId = route.params.publicId;
    userStore.publicId = route.params.publicId;
  }
  if (
    route.name === "financer-publicId" ||
    route.name === "financer" ||
    route.name === "financer-prescore" ||
    route.name === "financer-prescore-prescoreId" ||
    route.name === "financer-publicId-review" ||
    route.name === "financer-test" ||
    /^financer-\w*/g.test(route.name)
  ) {
    bnplApplicationStore.financer = route.params.financer;
    selfcareStore.financer = route.params.financer;

    if (process.client) {
      sessionStore.initStore(route.params.financer);
      Network.configure(sessionStore);
      if (sessionStore.isLoggedIn) {
        loadAllMessages(i18nInstance, countryStore.locale);
      } else {
        loadPublicMessages(i18nInstance, countryStore.locale);
      }
    }

    setI18nLanguage(i18nInstance, countryStore.locale + "!");
    setFormats(i18nInstance, countryStore.locale, countryStore.currency);

    if (brandingStore.logoCompactUrl) {
      useHead({
        link: [
          {
            rel: "icon",
            type: "image/x-icon",
            href: brandingStore.logoCompactUrl,
          },
        ],
      });
    }
  }
  if (route.name === "financer-prescore") {
    /* Get prescore parameters */
    let query = route.query;
    let paramsOk = query && query.offerId && query.merchantId;
    if (query.description && query.description.length > 255) {
      query.description = query.description.slice(0, 252) + "...";
    }
    const financier = route.params.financer;

    let queryByFinancier = JSON.parse(localStorage.getItem("prescore_query"));

    if (!paramsOk) {
      // try from localStorage
      if (queryByFinancier && queryByFinancier[financier]) {
        query = queryByFinancier[financier];
        paramsOk = query && query.offerId && query.merchantId;
      }
    } else {
      // save to localStorage
      if (queryByFinancier) {
        queryByFinancier[financier] = query;
      } else {
        queryByFinancier = {};
        queryByFinancier[financier] = query;
      }
      localStorage.setItem("prescore_query", JSON.stringify(queryByFinancier));
    }

    if (paramsOk) {
      cartStore.load({
        created: new Date().toLocaleDateString(),
        items: [],
        name: query.name,
        description: query.description,
        sum: query.amount,
      });
      prescoreStore.offerId = query.offerId;
      prescoreStore.redirectUrl = query.redirectUrl;
      prescoreStore.merchantId = query.merchantId;

      /* Get offer */
      try {
        prescoreStore.getOffer(query.offerId);
      } catch (e) {
        showError({
          statusCode: 404,
          statusMessage: "Wrong offer ID",
        });
      }
    } else {
      showError({
        statusCode: 400,
        statusMessage: "Missing required parameters",
      });
    }
  }
});
</script>
<style></style>
